'use strict';

/**
 * Shared, declarative description of the Phase-1/2 SDLC entities. One place to
 * say "a Vision has these columns, these editable fields, and these
 * relationships" — the generic providers, the EntityList view, the Board view,
 * and the EntityDetail view all read from here.
 *
 * Field shape:    { F: column, L: label, T: 'text'|'textarea'|'select'|'likert', O: [options] }
 *                 (Markdown: true renders the field's value as markdown on the detail page;
 *                  'likert' is a 0-5 score where 0 means unanswered)
 * Column shape:   { F, L, Fmt?: (row) => string, Badge?: true, DefaultHidden?: true, Key?: true }
 *                 A list has two modes, and each one is just its own set of visible columns:
 *                   table    the full intersection. Everything except DefaultHidden columns.
 *                   concise  the scan. The mark, the title, and the Key columns, nothing else.
 *                 So DefaultHidden is the table-mode default and Key is the concise-mode default.
 *                 Either can be overridden per user from the column picker, which edits whichever
 *                 mode is on screen; that is also how a person adds a column back into concise to
 *                 cross-reference something for a minute. The NumberField column (which renders as
 *                 the entity's mark, WI-014) and the TitleField column are always shown in both:
 *                 identity and name are not optional.
 *                 Badge tint: Badge with Tint?: (row) => '#hex' colors the pill.
 *                 Rich, view-resolved columns (the EntityList view fills these from
 *                 caches it prefetches): Assignee?: true (IDAssignedUser -> avatar +
 *                 name, hexagon for an AI agent), Target?: true (TargetType + the
 *                 typed FK -> the target's name), Sessions?: true (a linked-session
 *                 count the view stamps onto _SessionCount).
 * Relation shape: { Render: 'fk'|'link'|'inverse-fk'|'inverse-join'|'target', Label, ... }
 *                 (inverse-fk extras: SubField names the chip's sub-label column;
 *                  Create: { Label, Prefill } adds a create-from-here feedback button)
 *
 * Entity flags: NumberField (per-customer human number, shown in the detail title),
 * Moodboard / Comments / Steps (embedded sections on the detail page), NoCreate
 * (list hides "+ New"), HeaderActions ([{ Label, Method }] dispatched to the app).
 *
 * DefaultSort is not optional. Meadow returns rows oldest-first, so a list without one lands with
 * the newest thing at the bottom of the page, which is never what a person wants. Every list that
 * carries Columns declares one, and a test enforces it. Prefer a real date column where the list
 * shows one, and the entity's own number descending otherwise, so the ordering is legible in a
 * column the reader can actually see.
 *
 * Filter is the focus bar. Declare it to shape the bar by hand (WorkItem, Vision, Feedback); leave
 * it off and the list still gets a bar derived from its own Fields (text search, plus Status / Type
 * / Priority facets where those are select fields). Set it to false to opt out entirely.
 */

// The default board lanes. This is now a SEED, not the runtime source: a lane
// store (the BoardLane table) is seeded from this set on a tenant's first board
// load, and administrators manage the live set from Settings. The board and its
// API provider read the store; this array is the fallback when the store has no
// lanes yet (fresh install, or a non-authenticated/unit-test context). A future
// retold-workflow (P6) supplies workflow-definition-derived lanes to the same
// store rather than replacing this seam.
//
// Each lane carries a stable Key (the Status token stored on cards), a display
// Label (the Status enum is single-token 'InProgress'; the board renders the
// label so it reads "In Progress"), a Color, and IsTerminal (the Done-equivalent
// lane). Blocked lives at the end, not mid-flow: most cards never block, so it
// stays out of the natural Backlog -> ... -> Done reading order.
const DefaultBoardLanes =
[
	{ Key: 'Backlog',    Label: 'Backlog',     Color: '#8a94a6', IsTerminal: false },
	{ Key: 'Todo',       Label: 'Todo',        Color: '#5b6376', IsTerminal: false },
	{ Key: 'InProgress', Label: 'In Progress', Color: '#2988df', IsTerminal: false },
	{ Key: 'Review',     Label: 'Review',      Color: '#d99366', IsTerminal: false },
	{ Key: 'Done',       Label: 'Done',        Color: '#3fae6b', IsTerminal: true },
	{ Key: 'Blocked',    Label: 'Blocked',     Color: '#d0555b', IsTerminal: false }
];
// Back-compat views over the default set. BoardLanes (keys, in order) still feeds
// the Status <select> options on the entity detail editors; BoardLaneLabels maps
// key -> label. Derived so the seed stays the single source of truth.
const BoardLanes = DefaultBoardLanes.map((pLane) => pLane.Key);
const BoardLaneLabels = DefaultBoardLanes.reduce((pMap, pLane) => { pMap[pLane.Key] = pLane.Label; return pMap; }, {});

const Priorities = ['Low', 'Normal', 'High', 'Urgent'];
const WorkItemTypes = ['Triage', 'Research', 'Innovation', 'Issue', 'Debt', 'Enhancement'];
// The compact icon per WorkItem type: the Type column renders a glyph (with the type name as a
// tooltip) instead of the word, so a dense list spends its width on the Title. Glyphs are
// registered in PlansheetIcons; a type with no mapping falls back to its text in the cell.
const WorkItemTypeIcons = { Triage: 'Funnel', Research: 'Microscope', Innovation: 'Lightbulb', Issue: 'Bug', Debt: 'Hourglass', Enhancement: 'Sparkles' };
// The compact icon per Feedback source type (same idea as WorkItemTypeIcons): the Source column
// renders a glyph in place of a long phrase like "Customer Conversation", which is what made that
// column so wide (#252). A source with no mapping falls back to its text; the list's expanded
// density brings the words back alongside the glyph.
const FeedbackSourceIcons = { 'Test Run': 'Flask', 'Customer Conversation': 'Comments', 'Developer Experience': 'Terminal', 'Field Trial': 'Building', 'Other': 'Feather' };
const RelationTypes = ['DependsOn', 'Refines', 'Supersedes', 'Relates'];
const WorkOutcomes = ['Unknown', 'Delivered', 'Partial', 'Abandoned'];
// Why a Work Item did not pass QC when it is kicked back from Review. Rides on the
// qc.failed WorkEvent payload and drives the "Send back" reason picker.
const QCReasonCodes = ['Incomplete', 'Defect', 'WrongApproach', 'NeedsTests', 'NeedsDocs', 'CannotReproduce', 'Other'];
const MediaTypes = ['Screenshot', 'Photo', 'Drawing', 'Recording', 'HowTo', 'Other'];

// User Tasks kit vocabularies (V50). Status gates catalog visibility (only Active tasks are offered to
// users); OutcomeKind tells the UI how to render an answer.
const TaskStatuses = ['Draft', 'Active', 'Retired'];
const TaskOutcomeKinds = ['text', 'markdown', 'table', 'number', 'document', 'link'];
// A Request walks Pending -> Running as its node run works, then Answered on success (or Failed / Cancelled).
const RequestStatuses = ['Pending', 'Running', 'Answered', 'Failed', 'Cancelled'];

// Product Testing kit vocabularies. TestRunStep outcomes also allow '' (pending).
const RoutineStatuses = ['Draft', 'Active', 'Retired'];
const TestRunStatuses = ['In Progress', 'Passed', 'Failed', 'Abandoned'];
const TestRunStepOutcomes = ['Passed', 'Failed', 'Skipped'];
// The lifecycle of an As-Built: Built (session finished, image made) -> Deployed (running on dev) ->
// Validated (a passing dev TestRun of its routine) -> Promoted (that image digest pushed to prod).
// Superseded marks an older As-Built for the same work when a newer one takes its place.
// Delivered is the terminal state for work that shipped but does not ride this deploy pipeline: a
// commit landed in a sibling workspace can never be an ancestor of an app ref, so left at Built it
// would wait forever on a dev deploy that cannot pick it up. It is delivered, not retired, which is
// why Superseded was the wrong word for it.
const AsBuiltStatuses = ['Built', 'Deployed', 'Validated', 'Promoted', 'Superseded', 'Delivered'];
// A deploy request the Releases page queues and the executor runs: Requested -> Running -> Succeeded | Failed.
const DeployRequestStatuses = ['Requested', 'Running', 'Succeeded', 'Failed', 'Cancelled'];
// A registered node: Active claims work, Suspended claims nothing (WI #305, Feature 168).
const NodeRegistrationStatuses = ['Pending', 'Active', 'Suspended', 'Revoked'];
const WorkspaceStatuses = ['Active', 'Retired'];
// Standalone: its own clone, from RepoURL. MonorepoPath: a repository living at RepoPath inside the checkout
// of another workspace -- the shape that lets ONE root on a node cover every module in a monorepo.
const WorkspaceCheckoutStrategies = ['Standalone', 'MonorepoPath'];
// Read the roles a node published in its CapabilityJSON, for the list column.
const nodeRolesSummary = (pRow) =>
{
	try { let tmpCapability = JSON.parse(pRow.CapabilityJSON || '{}'); return (Array.isArray(tmpCapability.Roles) ? tmpCapability.Roles : []).join('+'); }
	catch (pError) { return ''; }
};
const nodeWorkspacesSummary = (pRow) =>
{
	try { let tmpCapability = JSON.parse(pRow.CapabilityJSON || '{}'); return (Array.isArray(tmpCapability.Workspaces) ? tmpCapability.Workspaces : []).join(', '); }
	catch (pError) { return ''; }
};
// Where this node says its checkouts are (WI #456). The plan sheet declares WHAT its repositories are; this
// is the per-node half -- WHERE they live on that machine -- so an operator maps one from this screen instead
// of SSH-ing in to edit agent-runner.json.
const nodeWorkspacePathsSummary = (pRow) =>
{
	try
	{
		let tmpCapability = JSON.parse(pRow.CapabilityJSON || '{}');
		let tmpPaths = tmpCapability.WorkspacePaths || {};
		return Object.keys(tmpPaths).map((pKey) =>
		{
			let tmpEntry = tmpPaths[pKey];
			return pKey + ' -> ' + ((typeof tmpEntry === 'string') ? tmpEntry : String((tmpEntry || {}).Path || ''));
		}).join(', ');
	}
	catch (pError) { return ''; }
};

// What this node was told about and could NOT find. The reason WI #456 exists: this used to be a line in a
// log on the box, so a declared gate sat inert for a day while the board said the work was Done. On screen it
// is the one thing an operator needs to see about a node that looks healthy and is not doing what was asked.
const nodeUnresolvedSummary = (pRow) =>
{
	try
	{
		let tmpCapability = JSON.parse(pRow.CapabilityJSON || '{}');
		let tmpUnresolved = Array.isArray(tmpCapability.UnresolvedWorkspaces) ? tmpCapability.UnresolvedWorkspaces : [];
		if (!tmpUnresolved.length) { return ''; }
		return tmpUnresolved.map((pEntry) => String((pEntry || {}).Key || '')).filter((pKey) => pKey).join(', ');
	}
	catch (pError) { return ''; }
};

const FeedbackSourceTypes = ['Test Run', 'Customer Conversation', 'Developer Experience', 'Field Trial', 'Other'];
const KeystoneCustomerStatuses = ['Prospect', 'Active', 'Former'];
const KeystoneCustomerTiers = ['Keystone', 'Standard'];

// Issue tracking vocabularies. Status walks Open -> Closed; WontFix and Deferred are side states.
// The closed-ish set (Fixed/Verified/Closed/WontFix) is what stamps ResolvedDate server-side.
const IssueStatuses = ['Open', 'Triaged', 'InProgress', 'Fixed', 'Verified', 'Closed', 'WontFix', 'Deferred'];
const IssueSeverities = ['Minor', 'Major', 'Critical'];
// How much to trust the report itself (distinct from Status = resolution, Severity = impact).
// Drives how a linked issue is weighted as supporting evidence in a generated AI prompt.
// Reported = an unverified claim (treat as possibly-not-factual); Corroborated = independently
// supported by more than one source; Verified = confirmed factual by the team.
const IssueReliabilities = ['Reported', 'Corroborated', 'Verified'];
const ContactKinds = ['Person', 'Shop', 'Reader', 'Org'];
const CallChannels = ['Phone', 'Email', 'Chat', 'InPerson', 'Other'];
const CallDirections = ['Inbound', 'Outbound'];

// Deliver vocabularies. Expectation status is a light three-state; Deliverable stages are the fixed
// delivery pipeline (a Drafting Kit renames the labels in B5). Stored as a single-token Status.
const ExpectationStatuses = ['Open', 'PartiallyMet', 'Met'];
const DeliverableStages = ['Scoped', 'Built', 'Delivered', 'Installed', 'Accepted'];

// Display a 0-5 likert score (0 or empty means unanswered).
function formatLikert(pValue)
{
	let tmpScore = Number(pValue) || 0;
	if (tmpScore < 1) { return 'not answered'; }
	if (tmpScore > 5) { tmpScore = 5; }
	return '●'.repeat(tmpScore) + '○'.repeat(5 - tmpScore) + ' ' + tmpScore + '/5';
}

// A stored ISO datetime as a short local calendar date (YYYY-MM-DD) for a list
// cell. Blank for an empty / unparseable value. The cell displays this, but the
// column still sorts on the raw ISO string, which orders chronologically.
function formatDate(pValue)
{
	if (!pValue) { return ''; }
	let tmpDate = new Date(pValue);
	if (isNaN(tmpDate.getTime())) { return ''; }
	let tmpMonth = String(tmpDate.getMonth() + 1).padStart(2, '0');
	let tmpDay = String(tmpDate.getDate()).padStart(2, '0');
	return tmpDate.getFullYear() + '-' + tmpMonth + '-' + tmpDay;
}

// A stored ISO datetime as a compact relative age ('just now', '5m', '3h', '2d',
// '4w') for a list cell, so a person reads "how fresh is this" at a glance. Past a
// month it falls back to the calendar date (relative gets useless that far out).
// The cell shows this, but the column still sorts on the raw ISO string.
function formatRelative(pValue)
{
	if (!pValue) { return ''; }
	let tmpThen = Date.parse(String(pValue));
	if (isNaN(tmpThen)) { return ''; }
	let tmpDiff = Date.now() - tmpThen;
	if (tmpDiff < 0) { tmpDiff = 0; }
	let tmpSec = Math.floor(tmpDiff / 1000);
	if (tmpSec < 45) { return 'just now'; }
	let tmpMin = Math.floor(tmpSec / 60);
	if (tmpMin < 60) { return tmpMin + 'm ago'; }
	let tmpHour = Math.floor(tmpMin / 60);
	if (tmpHour < 24) { return tmpHour + 'h ago'; }
	let tmpDay = Math.floor(tmpHour / 24);
	if (tmpDay < 7) { return tmpDay + 'd ago'; }
	let tmpWeek = Math.floor(tmpDay / 7);
	if (tmpDay < 35) { return tmpWeek + 'w ago'; }
	return formatDate(pValue);
}

// The board lane color for a Status token (drives the Status badge tint on the list,
// so a list row and its board card read the same color). Falls back to a neutral grey.
function laneColor(pStatus)
{
	let tmpLane = DefaultBoardLanes.find((pLane) => pLane.Key === String(pStatus || ''));
	return tmpLane ? tmpLane.Color : '#8a94a6';
}

// The tint for a Priority badge: quiet for the low end, warm/hot as it climbs, so
// Urgent and High catch the eye in a dense list. Unknown priorities read neutral.
const _PriorityColors = { Low: '#8a94a6', Normal: '#2988df', High: '#d99366', Urgent: '#d0555b' };
function priorityColor(pPriority)
{
	return _PriorityColors[String(pPriority || '')] || '#8a94a6';
}

// The compact list-cell form of a Feedback row's three scores
// (expected/intuitive/enjoyment), e.g. '5/4/4' with '-' for unanswered.
function formatLikertShort(pRow)
{
	let fOne = (pValue) =>
	{
		let tmpScore = Number(pValue) || 0;
		return (tmpScore >= 1) ? String(Math.min(tmpScore, 5)) : '-';
	};
	let tmpCells = [fOne(pRow.ScoreDidWhatExpected), fOne(pRow.ScoreIntuitive), fOne(pRow.ScoreEnjoyment)];
	if (tmpCells.join('') === '---') { return 'not scored'; }
	return tmpCells.join('/');
}

// Join tables (thin link rows). A = the "owning"/source FK, B = the target FK.
const Joins =
{
	FeatureVision: { Entity: 'FeatureVisionJoin', Plural: 'FeatureVisionJoins', ID: 'IDFeatureVisionJoin', A: 'IDFeature', B: 'IDVision' },
	FeatureModule: { Entity: 'FeatureModuleJoin', Plural: 'FeatureModuleJoins', ID: 'IDFeatureModuleJoin', A: 'IDFeature', B: 'IDModule' },
	VisionVision:  { Entity: 'VisionVisionJoin', Plural: 'VisionVisionJoins', ID: 'IDVisionVisionJoin', A: 'IDVisionSource', B: 'IDVisionTarget' },
	IssueWorkItem: { Entity: 'IssueWorkItemJoin', Plural: 'IssueWorkItemJoins', ID: 'IDIssueWorkItemJoin', A: 'IDIssue', B: 'IDWorkItem' },
	SessionWorkItem: { Entity: 'SessionFileWorkItem', Plural: 'SessionFileWorkItems', ID: 'IDSessionFileWorkItem', A: 'IDSessionFile', B: 'IDWorkItem' },
	FeedbackWorkItem: { Entity: 'FeedbackWorkItemJoin', Plural: 'FeedbackWorkItemJoins', ID: 'IDFeedbackWorkItemJoin', A: 'IDFeedback', B: 'IDWorkItem' }
};

const Entities =
{
	Vision:
	{
		// Render this entity as a Blueprint: a drafting title block coalescing its linked
		// artifacts. Backed by a real Blueprint record it owns, gathered from the same connections
		// manifest the grid reads, so the two can never disagree (Vision #46, WI #340).
		Blueprint: true,
		Key: 'Vision', Plural: 'Visions', IDField: 'IDVision', Title: 'Visions', TitleField: 'Name', Icon: 'Eye',
		NumberField: 'VisionNumber', Designator: 'V',
		// Fold this entity's links into the cohesive connections grid (replaces the stacked relation sections).
		Connections: true,
		// Embed an inspiration moodboard on the detail page (Moodboard.IDVision-owned). EntityDetail
		// renders a board container and mounts the shared Vision-Board view; edits need content.edit.
		Moodboard: true,
		// Let a vision pick its Drafting Kit (an imported/owned VisionType) so its pages and its work
		// items' blueprints speak that craft. EntityDetail shows a craft picker in the header.
		Craft: true,
		// Embed a comments section on the detail page (CommentThread owned by OwnerType 'Vision').
		Comments: true,
		// Show the four refinement loops (Design, Build, Deliver, Gather Feedback) for this vision.
		Loops: true,
		// Land the list on most-recently-touched first (not database order); the user can
		// re-sort by clicking any column header, and that choice is remembered per list.
		DefaultSort: 'UpdateDate:desc',
		// A light focus bar: free-text search over Name/Summary/Description, the Status
		// lanes as toggle chips, and an activity window. No saved views / role presets
		// (those are WorkItem-shaped), so the picker and its Save button stay hidden.
		Filter: { Search: true, Facets: ['Status'], Recent: true },
		Columns:
		[
			{ F: 'VisionNumber', L: '#' },
			{ F: 'Name', L: 'Name' },
			{ F: 'Scope', L: 'Scope' },
			{ F: 'Status', L: 'Status', Key: true },
			{ F: 'UpdateDate', L: 'Updated', Fmt: (pRow) => formatDate(pRow.UpdateDate), Key: true },
			// Created is real data but noise on the default view; off until the user turns it on.
			{ F: 'CreateDate', L: 'Created', Fmt: (pRow) => formatDate(pRow.CreateDate), DefaultHidden: true }
		],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Summary', L: 'Summary', T: 'text' },
			{ F: 'Description', L: 'Description', T: 'textarea' },
			{ F: 'Scope', L: 'Scope', T: 'text' },
			{ F: 'Status', L: 'Status', T: 'select', O: ['Draft', 'Active', 'Realized', 'Abandoned'] }
		],
		Relations:
		[
			{ Render: 'core-doc', Label: 'Core vision document' },
			{ Render: 'docs', Label: 'Dossier' },
			{ Render: 'media', Label: 'Media library' },
			{ Render: 'subvisions', Label: 'Parts of this vision', Join: 'VisionVision' },
			{ Render: 'link', Label: 'Related visions', Join: 'VisionVision', SelfField: 'IDVisionSource', TargetField: 'IDVisionTarget', Target: 'Vision', RelationType: true },
			{ Render: 'inverse-join', Label: 'Realized by features', Join: 'FeatureVision', SelfField: 'IDVision', MapField: 'IDFeature', Target: 'Feature' },
			{ Render: 'inverse-fk', Label: 'Work items about this', Source: 'WorkItem', SourceField: 'IDTargetVision' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	Module:
	{
		// Render this entity as a Blueprint: a drafting title block coalescing its linked
		// artifacts. Backed by a real Blueprint record it owns, gathered from the same connections
		// manifest the grid reads, so the two can never disagree (Vision #46, WI #340).
		Blueprint: true,
		Key: 'Module', Plural: 'Modules', IDField: 'IDModule', Title: 'Modules', TitleField: 'Name', Icon: 'Box',
		NumberField: 'ModuleNumber', Designator: 'M',
		Connections: true,
		// Embed whiteboards on the detail page (Moodboard owned by OwnerType 'Module'). A module can hold
		// several; the section lists them with a picker and lets you attach an existing standalone board.
		Moodboard: true,
		// No date column on this list, so order by the module's own number descending: newest first, and
		// visibly so, in a column the reader can see.
		DefaultSort: 'ModuleNumber:desc',
		Columns: [{ F: 'ModuleNumber', L: '#' }, { F: 'Name', L: 'Name' }, { F: 'Language', L: 'Language' }, { F: 'Status', L: 'Status', Key: true }],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Description', L: 'Description', T: 'textarea' },
			{ F: 'RepoURL', L: 'Repo URL', T: 'text' },
			{ F: 'RepoPath', L: 'Repo Path', T: 'text' },
			// Which repository this module is built in. This is the link a Work Item resolves through to
			// find the repository a node should work in (WorkItem -> Feature -> Module -> Workspace), so
			// leaving it off the form made the workspace ceiling unenforceable from the interface: the
			// column existed, Workspace-Service read it, and nothing a person could reach ever wrote it.
			{ F: 'IDWorkspace', L: 'Built in', T: 'select', E: 'Workspace' },
			{ F: 'Language', L: 'Language', T: 'text' },
			{ F: 'Status', L: 'Status', T: 'select', O: ['Active', 'Planned', 'Deprecated'] }
		],
		Relations:
		[
			{ Render: 'inverse-fk', Label: 'Features in this module', Source: 'Feature', SourceField: 'IDModule' },
			{ Render: 'inverse-fk', Label: 'Work items about this', Source: 'WorkItem', SourceField: 'IDTargetModule' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	Feature:
	{
		// Render this entity as a Blueprint: a drafting title block coalescing its linked
		// artifacts. Backed by a real Blueprint record it owns, gathered from the same connections
		// manifest the grid reads, so the two can never disagree (Vision #46, WI #340).
		Blueprint: true,
		Key: 'Feature', Plural: 'Features', IDField: 'IDFeature', Title: 'Features', TitleField: 'Name', Icon: 'Star',
		NumberField: 'FeatureNumber', Designator: 'F',
		Connections: true,
		// Embed whiteboards on the detail page (Moodboard owned by OwnerType 'Feature'). A feature can hold
		// several; the section lists them with a picker and lets you attach an existing standalone board.
		Moodboard: true,
		DefaultSort: 'FeatureNumber:desc',
		Columns: [{ F: 'FeatureNumber', L: '#' }, { F: 'Name', L: 'Name' }, { F: 'Status', L: 'Status', Key: true }, { F: 'Priority', L: 'Priority' }],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Description', L: 'Description', T: 'textarea' },
			{ F: 'Status', L: 'Status', T: 'select', O: BoardLanes },
			{ F: 'Priority', L: 'Priority', T: 'select', O: Priorities }
		],
		Relations:
		[
			{ Render: 'fk', Label: 'Built in module', Field: 'IDModule', Target: 'Module' },
			{ Render: 'link', Label: 'Realizes visions', Join: 'FeatureVision', SelfField: 'IDFeature', TargetField: 'IDVision', Target: 'Vision' },
			{ Render: 'inverse-fk', Label: 'Work items about this', Source: 'WorkItem', SourceField: 'IDTargetFeature' },
			{ Render: 'sessions', Label: 'Claude sessions', Field: 'IDFeature' },
			{ Render: 'media', Label: 'Screenshots & recordings' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	WorkItem:
	{
		Key: 'WorkItem', Plural: 'WorkItems', IDField: 'IDWorkItem', Title: 'Work Items', TitleField: 'Title', Icon: 'Ticket',
		NumberField: 'WorkItemNumber', Designator: 'WI',
		Connections: true,
		// Embed whiteboards on the detail page (Moodboard owned by OwnerType 'WorkItem'). A work item
		// can hold several; the section lists them with a picker and a "new" button.
		Moodboard: true,
		// Embed a comments section on the detail page (CommentThread owned by OwnerType 'WorkItem').
		Comments: true,
		// Embed the shared (team / blueprint-visible) checklists on the detail page (Checklist owned by
		// OwnerType 'WorkItem'). Private personal lists live on the Checklists page, not here.
		Checklist: true,
		// Embed the attached-prompts panel (PromptLink owned by OwnerType 'WorkItem'):
		// attach a prompt at a pinned version, run it, capture outcomes, refine.
		Prompts: true,
		// Render this work item as a Blueprint: a drafting title block coalescing its
		// linked artifacts (auto-gathered + curated). Mounted in onAfterRender.
		Blueprint: true,
		// Embed the Story panel: a chronological timeline of the Claude sessions that
		// advanced this work item (union of the scalar link + SessionFileWorkItem join),
		// so several sessions read as one narrative. Fed by GET /1.0/WorkItem/:id/Story.
		Story: true,
		// Review history: the QC decisions on this work item (the "did not pass QC" events with
		// their reason + note + linked issues), read from the workflow WorkEvent timeline.
		ReviewHistory: true,
		// Generate a prompt for this work item from a shape (pre-filled + attached), so a
		// session can be pointed at it by plan sheet Code + work item number over the MCP.
		HeaderActions: [{ Label: 'Assign', Method: 'assignWorkItem' }, { Label: 'Generate prompt', Method: 'generatePromptForWorkItem' }, { Label: 'Link issues', Method: 'linkIssuesToWorkItem' }, { Label: 'Link feedback', Method: 'linkFeedbackToWorkItem' }],
		// A soft-delete button in the header (meadow Deleted/DeleteDate; the WorkItemNumber
		// sequence ignores Deleted rows on purpose, so this never disturbs numbering).
		Deletable: true,
		// Saved, role-based focus views: the list gets a filter bar (facets + text search +
		// recent-activity window + a saved-view picker with shipped role presets). Facet option
		// lists come from the Fields' O arrays (Status/Type/Priority); Assignee pulls the team
		// roster (so the human/AI lens works); Target is the WorkItem's About type.
		// Land on the activity lens by default (#236): in-flight work plus anything touched in the
		// last few days, newest first -- not the whole backlog in database order. A person can still
		// switch to any view, "Views: none", or pin their own default per screen.
		Filter: { Search: true, Facets: ['Status', 'Type', 'Priority', 'Assignee', 'Target', 'WorkflowStatus'], Recent: true, Views: true, Assignee: true, Defaults: { activity: 'related', sort: 'UpdateDate:desc', view: 'activity' } },
		DefaultSort: 'UpdateDate:desc',
		Columns:
		[
			{ F: 'WorkItemNumber', L: '#' },
			{ F: 'Title', L: 'Title' },
			// Type as a compact glyph (a funnel for Triage, a bug for Issue, ...) with the type
			// name as its tooltip, so the column costs an icon's width, not a word's (#236).
			{ F: 'Type', L: 'Type', Icon: (pRow) => WorkItemTypeIcons[pRow.Type] || '', IconTitle: (pRow) => pRow.Type || '' },
			// Status + Priority as color-tinted badges: the lane color for Status (a row and
			// its board card read the same), a warm-as-it-climbs scale for Priority.
			{ F: 'Status', L: 'Status', Badge: true, Tint: (pRow) => laneColor(pRow.Status), Key: true },
			{ F: 'Priority', L: 'Priority', Badge: true, Tint: (pRow) => priorityColor(pRow.Priority) },
			// Who owns it (human or AI agent), resolved from the roster in the view and drawn
			// as an avatar + name -- a round avatar for a person, a hexagon for an AI agent.
			{ F: 'IDAssignedUser', L: 'Assignee', Assignee: true },
			// What the work item is about, resolved from the Vision/Module/Feature caches. Off by
			// default (#236): the default list stays lean; turn it on from the Columns menu.
			{ F: 'TargetType', L: 'Target', Target: true, DefaultHidden: true },
			// How many Claude sessions have advanced it (its auditable trail). The view stamps
			// the count onto _SessionCount so this column both renders and sorts on it. Off by
			// default (#236): a niche signal; turn it on from the Columns menu when needed.
			{ F: '_SessionCount', L: 'Sessions', Sessions: true, DefaultHidden: true, Computed: true },
			// Freshness at a glance: a relative age ('3h ago'), sorting on the raw UpdateDate.
			{ F: 'UpdateDate', L: 'Updated', Fmt: (pRow) => formatRelative(pRow.UpdateDate), Key: true }
		],
		Fields:
		[
			{ F: 'Title', L: 'Title', T: 'text' },
			{ F: 'Type', L: 'Type', T: 'select', O: WorkItemTypes },
			{ F: 'Description', L: 'Description', T: 'textarea' },
			{ F: 'Status', L: 'Status', T: 'select', O: BoardLanes },
			{ F: 'Priority', L: 'Priority', T: 'select', O: Priorities },
			// The one lifecycle date a person sets by hand (a target). WorkStartedDate,
			// DeliveryDate, and ValidationDate are stamped by the server on status changes.
			{ F: 'EstimatedDeliveryDate', L: 'Estimated delivery', T: 'date' }
		],
		Relations:
		[
			{ Render: 'target', Label: 'This work item is about' },
			{ Render: 'link', Label: 'Issues this addresses', Join: 'IssueWorkItem', SelfField: 'IDWorkItem', TargetField: 'IDIssue', Target: 'Issue' },
			{ Render: 'link', Label: 'Feedback that drove this', Join: 'FeedbackWorkItem', SelfField: 'IDWorkItem', TargetField: 'IDFeedback', Target: 'Feedback' },
			{ Render: 'sessions', Label: 'Claude sessions', Field: 'IDWorkItem' },
			{ Render: 'media', Label: 'Screenshots & recordings' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	Routine:
	{
		Key: 'Routine', Plural: 'Routines', IDField: 'IDRoutine', Title: 'Routines', TitleField: 'Name', Icon: 'ListOrdered',
		NumberField: 'RoutineNumber', Designator: 'RTN',
		Connections: true,
		// Embed the ordered step editor on the detail page (the Plansheet-RoutineSteps
		// view renders into #Routine-Steps). Editing needs content.edit.
		Steps: true,
		// Embed a comments section on the detail page (CommentThread owned by OwnerType 'Routine').
		Comments: true,
		// Start a TestRun of this routine (POST /1.0/TestRun/Start) and land in the runner.
		HeaderActions: [{ Label: 'Start test run', Method: 'startTestRun' }],
		DefaultSort: 'RoutineNumber:desc',
		Columns: [{ F: 'RoutineNumber', L: '#' }, { F: 'Name', L: 'Name' }, { F: 'Status', L: 'Status', Badge: true, Key: true }],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Summary', L: 'Summary', T: 'text' },
			{ F: 'Description', L: 'Description', T: 'textarea', Markdown: true },
			{ F: 'Status', L: 'Status', T: 'select', O: RoutineStatuses }
		],
		Relations:
		[
			{ Render: 'inverse-fk', Label: 'Test runs of this routine', Source: 'TestRun', SourceField: 'IDRoutine', SubField: 'Status' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	// A tenant's user-facing task (V50). The catalog entry an admin authors: a name and description, the
	// audience it is for, the target node capability + action, and the prompt shape that assembles the user's
	// input. Not Connections, so the fk stacked-picker below is the live editor for the input template. Users
	// file Requests against a Task from the Ask area (a separate user-facing view), never from here.
	Task:
	{
		Key: 'Task', Plural: 'Tasks', IDField: 'IDTask', Title: 'Tasks', TitleField: 'Name', Icon: 'Sparkles',
		NumberField: 'TaskNumber', Designator: 'TSK',
		Comments: true,
		DefaultSort: 'TaskNumber:desc',
		Columns:
		[
			{ F: 'TaskNumber', L: '#' },
			{ F: 'Name', L: 'Name' },
			{ F: 'Audience', L: 'Audience' },
			{ F: 'Capability', L: 'Capability', DefaultHidden: true },
			{ F: 'Status', L: 'Status', Badge: true, Key: true }
		],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Summary', L: 'Summary', T: 'text' },
			{ F: 'Description', L: 'Description', T: 'textarea', Markdown: true },
			{ F: 'Audience', L: 'Audience', T: 'text' },
			{ F: 'Capability', L: 'Node capability', T: 'combo', Source: 'NodeCapabilities', SourceKey: 'Capability' },
			{ F: 'Action', L: 'Action', T: 'combo', Source: 'NodeCapabilities', SourceKey: 'Action', DependsOn: 'Capability' },
			{ F: 'OutcomeKind', L: 'Outcome kind', T: 'select', O: TaskOutcomeKinds },
			{ F: 'OutcomeTargetJSON', L: 'Output folder (document tasks)', T: 'text' },
			{ F: 'Status', L: 'Status', T: 'select', O: TaskStatuses },
			{ F: 'Conversational', L: 'Multi-turn chat', T: 'checkbox' },
			{ F: 'AllowAttachments', L: 'Allow file attachments', T: 'checkbox' }
		],
		Relations:
		[
			{ Render: 'fk', Label: 'Input template (prompt shape)', Field: 'IDPromptShape', Target: 'PromptShape' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	// A prompt shape as a pickable entity. It is NOT given a list/detail route or a nav entry (its own editor
	// lives at /prompt-shapes); this descriptor exists only so the generic EntityAPI provider is created and a
	// Task's "Input template" fk picker can search the tenant's shapes by name.
	PromptShape:
	{
		Key: 'PromptShape', Plural: 'PromptShapes', IDField: 'IDPromptShape', Title: 'Prompt Shapes', TitleField: 'Name', Icon: 'PenRuler',
		// Pick-only target: no list/detail route and no + New, and (like DeployRequest/Session) no docs
		// surface -- its own editor at /prompt-shapes owns authoring. This descriptor exists only so the
		// generic EntityAPI provider is created and a Task's "Input template" fk picker can search shapes.
		Browse: true,
		NoCreate: true,
		DefaultSort: 'Name:asc',
		Columns: [{ F: 'Name', L: 'Name', Key: true }, { F: 'Scope', L: 'Scope' }, { F: 'Status', L: 'Status', Badge: true }],
		// Name and Description are the shape's prose columns; both are declared so search can look in them
		// (the FocusQuery prose-coverage invariant) even though this entity has no list of its own.
		Fields: [{ F: 'Name', L: 'Name', T: 'text' }, { F: 'Description', L: 'Description', T: 'textarea' }]
	},
	// A user's filed instance of a Task and the durable record of the question and its answer (V50). Created by
	// POST /1.0/Request/Submit (never the + New form) and answered by a node, so it is browse-only. The answer
	// renders from Outcome; IDTask is surfaced so a reader can open the task it came from.
	Request:
	{
		Key: 'Request', Plural: 'Requests', IDField: 'IDRequest', Title: 'Requests', TitleField: 'Name', Icon: 'CommentDots',
		NumberField: 'RequestNumber', Designator: 'REQ',
		Browse: true,
		NoCreate: true,
		DefaultSort: 'RequestNumber:desc',
		EmptyText: 'No requests yet. Run a task from the Ask area.',
		Columns:
		[
			{ F: 'RequestNumber', L: '#' },
			{ F: 'Name', L: 'Name' },
			{ F: 'Status', L: 'Status', Badge: true, Key: true },
			{ F: 'AnsweredDate', L: 'Answered', Fmt: (pRow) => pRow.AnsweredDate ? String(pRow.AnsweredDate).slice(0, 10) : '' }
		],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'AssembledInput', L: 'Question', T: 'textarea' },
			{ F: 'Outcome', L: 'Answer', T: 'textarea', Markdown: true },
			{ F: 'Status', L: 'Status', T: 'select', O: RequestStatuses }
		],
		Relations:
		[
			{ Render: 'fk', Label: 'Task', Field: 'IDTask', Target: 'Task' }
		]
	},
	// The turns of a conversational Request (Ask multi-turn). Provider-only: no list/detail route and no + New
	// (a user turn is posted via POST /1.0/Request/:id/Message, which also dispatches the run, and the turns are
	// read back by the Ask chat view). This descriptor exists so the generic EntityAPI provider (RequestMessageAPI)
	// is created to list a conversation's turns (resolve by IDRequest) and read one.
	RequestMessage:
	{
		Key: 'RequestMessage', Plural: 'RequestMessages', IDField: 'IDRequestMessage', Title: 'Messages', TitleField: 'Content', Icon: 'CommentDots',
		Browse: true,
		NoCreate: true,
		DefaultSort: 'Sequence:asc',
		Columns: [{ F: 'Sequence', L: '#' }, { F: 'Content', L: 'Message' }, { F: 'Role', L: 'Role' }, { F: 'Status', L: 'Status', Badge: true, Key: true }],
		Fields: [{ F: 'Content', L: 'Message', T: 'textarea', Markdown: true }, { F: 'Role', L: 'Role', T: 'text' }]
	},
	// A unit of work built from a cluster of source files (V54). Its own view (route /projects) owns the corpus,
	// the task launcher, the conversation, and the blueprint; this descriptor creates the ProjectAPI provider and
	// a basic browse surface for it.
	Project:
	{
		Key: 'Project', Plural: 'Projects', IDField: 'IDProject', Title: 'Projects', TitleField: 'Name', Icon: 'FolderOpen',
		NumberField: 'ProjectNumber', Designator: 'PRJ',
		Comments: true,
		DefaultSort: 'ProjectNumber:desc',
		Columns:
		[
			{ F: 'ProjectNumber', L: '#' },
			{ F: 'Name', L: 'Name' },
			{ F: 'Kind', L: 'Kind' },
			{ F: 'Status', L: 'Status', Badge: true, Key: true }
		],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Summary', L: 'Summary', T: 'text' },
			{ F: 'Description', L: 'Description', T: 'textarea', Markdown: true },
			{ F: 'Kind', L: 'Kind', T: 'text' },
			{ F: 'Status', L: 'Status', T: 'text' }
		],
		Relations:
		[
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	// A Project's corpus item (V54). Provider-only: the Project view manages the corpus. Exists so the generic
	// ProjectSourceAPI provider lists a project's items (resolve by IDProject) and reads one.
	ProjectSource:
	{
		Key: 'ProjectSource', Plural: 'ProjectSources', IDField: 'IDProjectSource', Title: 'Sources', TitleField: 'Name', Icon: 'FileText',
		Browse: true,
		NoCreate: true,
		DefaultSort: 'SortOrder:asc',
		Columns: [{ F: 'Role', L: 'Role' }, { F: 'Name', L: 'Name', Key: true }],
		// Description and SortOrder are declared so search can look in the prose (FocusQuery prose-coverage
		// invariant) and so the SortOrder default sort names a real field, even though the Project view --
		// not a generic form -- authors a corpus item.
		Fields: [{ F: 'Role', L: 'Role', T: 'text' }, { F: 'Name', L: 'Name', T: 'text' }, { F: 'Description', L: 'Description', T: 'textarea' }, { F: 'SortOrder', L: 'Order', T: 'number' }]
	},
	// A configurable metadata form (a manyfest manifest) selected by (EntityType, Kind) with a per-tenant
	// override. Edited through the generic entity surface -- ManifestJSON is a code field, not a textarea, for
	// the same reason CapabilityJSON is: hand-balancing JSON braces in a plain box is how a typo becomes a form
	// that silently will not render. Kind blank means the manifest applies to any Kind of its EntityType.
	MetadataManifest:
	{
		Key: 'MetadataManifest', Plural: 'MetadataManifests', IDField: 'IDMetadataManifest', Title: 'Metadata forms', TitleField: 'Name', Icon: 'FileText',
		Browse: true,
		DefaultSort: 'EntityType:asc',
		Columns:
		[
			{ F: 'EntityType', L: 'Entity' },
			{ F: 'Kind', L: 'Kind' },
			{ F: 'Name', L: 'Name', Key: true },
			{ F: 'Version', L: 'Ver' },
			{ F: 'Status', L: 'Status', Badge: true }
		],
		Fields:
		[
			{ F: 'EntityType', L: 'Entity type', T: 'text' },
			{ F: 'Kind', L: 'Kind (blank = any)', T: 'text' },
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Status', L: 'Status', T: 'text' },
			{ F: 'ManifestJSON', L: 'Manifest (JSON)', T: 'code', Language: 'json' }
		]
	},
	// A tenant's reusable Project shape (V54 / F163), mirroring VisionType for Visions. Authored conversationally
	// (the Create / Adjust Project Type loop) and committed behind consent, so there is no + New form here --
	// NoCreate. Browse-listed so the Project Types screen and the generic surface can read them. The form shape
	// itself lives on the linked MetadataManifest row (external and versioned), so adjusting a type never orphans
	// a Project's saved MetadataJSON. Description is declared for prose search coverage and SortOrder so the
	// default sort names a real field, even though the type is authored through the screen, not this form.
	ProjectType:
	{
		Key: 'ProjectType', Plural: 'ProjectTypes', IDField: 'IDProjectType', Title: 'Project Types', TitleField: 'Name', Icon: 'FileText',
		Browse: true,
		NoCreate: true,
		DefaultSort: 'SortOrder:asc',
		Columns:
		[
			{ F: 'Name', L: 'Name' },
			{ F: 'TypeKey', L: 'Key' },
			{ F: 'Status', L: 'Status', Badge: true, Key: true }
		],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'TypeKey', L: 'Key', T: 'text' },
			{ F: 'Description', L: 'Description', T: 'textarea' },
			{ F: 'Status', L: 'Status', T: 'text' },
			{ F: 'SortOrder', L: 'Order', T: 'number' }
		]
	},
	TestRun:
	{
		Key: 'TestRun', Plural: 'TestRuns', IDField: 'IDTestRun', Title: 'Test Runs', TitleField: 'Name', Icon: 'Flask',
		NumberField: 'TestRunNumber', Designator: 'TR',
		// Runs start from a Routine's "Start test run" action, never the + New form;
		// the detail surface is the runner view (route /test-run/:id), not EntityDetail.
		NoCreate: true,
		EmptyText: 'No runs yet. Open a routine and click "Start test run".',
		// Newest run first. CompletedDate is the obvious date column but it is null for anything still
		// running, which would bury exactly the run a person came here to watch.
		DefaultSort: 'TestRunNumber:desc',
		Columns:
		[
			{ F: 'TestRunNumber', L: '#' },
			{ F: 'Name', L: 'Name' },
			{ F: 'Status', L: 'Status', Badge: true, Key: true },
			{ F: 'Environment', L: 'Environment' },
			{ F: 'CompletedDate', L: 'Completed', Fmt: (pRow) => pRow.CompletedDate ? String(pRow.CompletedDate).slice(0, 10) : '' }
		],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Environment', L: 'Environment', T: 'text' },
			{ F: 'Summary', L: 'Summary', T: 'textarea' },
			{ F: 'Status', L: 'Status', T: 'select', O: TestRunStatuses }
		],
		Relations:
		[
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	Feedback:
	{
		Key: 'Feedback', Plural: 'Feedbacks', IDField: 'IDFeedback', Title: 'Feedback', TitleField: 'Title', Icon: 'MessageDots',
		NumberField: 'FeedbackNumber', Designator: 'FB',
		Connections: true,
		// Embed a comments section on the detail page (CommentThread owned by OwnerType 'Feedback').
		Comments: true,
		// Promote a piece of feedback into a tracked WorkItem, from the detail header.
		HeaderActions: [{ Label: 'Spawn work item', Method: 'spawnWorkItemFromFeedback' }],
		// Surface the prompts of the work items this feedback drove (read-only), so the feedback page is
		// a command center: read + copy the prompt / MCP run command without leaving. Mounted below the
		// connections grid; the prompts stay owned by their work item (a display through the relation).
		WorkItemPrompts: true,
		// A growing feedback backlog stays scannable: a piece can be marked Addressed
		// (a soft "handled" flag), which the list hides by default. Drives the list's
		// Address/Reopen quick action and the detail's badge + toggle. AddressedDate
		// stamps when it was marked and clears on reopen.
		Addressable: { Field: 'Addressed', DateField: 'AddressedDate', AddressLabel: 'Mark addressed', ReopenLabel: 'Reopen' },
		// Focus-view filter bar: default to unaddressed only (the point of #201), with
		// facets to widen to all feedback, a specific keystone customer, or a source type,
		// plus text search over Title/Body. Customer:true loads the customer roster for the
		// facet options; no saved-view picker (the WorkItem role presets do not apply here).
		Filter: { Search: true, Facets: ['Addressed', 'Customer', 'SourceType'], Defaults: { addressed: 'no' }, Customer: true },
		DefaultSort: 'FeedbackNumber:desc',
		Columns:
		[
			{ F: 'FeedbackNumber', L: '#' },
			{ F: 'Title', L: 'Title' },
			// Source as a compact glyph (a flask for Test Run, a terminal for Developer Experience, ...)
			// with the source name as its tooltip -- and, in the list's expanded density, alongside the
			// word. Icon-only by default is what tames the once-very-wide Source column (#252).
			{ F: 'SourceType', L: 'Source', Icon: (pRow) => FeedbackSourceIcons[pRow.SourceType] || '', IconTitle: (pRow) => pRow.SourceType || '', Key: true },
			{ F: 'ScoreDidWhatExpected', L: 'Scores', Fmt: formatLikertShort }
		],
		Fields:
		[
			{ F: 'Title', L: 'Title', T: 'text' },
			{ F: 'SourceType', L: 'Source type', T: 'select', O: FeedbackSourceTypes },
			// The likert scores fit product-trial feedback; a Developer Experience note (a dev flagging a
			// bug or rough edge) should not be forced to rate enjoyment, so hide them for that source.
			{ F: 'ScoreDidWhatExpected', L: 'Did what was expected', T: 'likert', HideWhen: { Field: 'SourceType', Equals: 'Developer Experience' } },
			{ F: 'ScoreIntuitive', L: 'Intuitive to use', T: 'likert', HideWhen: { Field: 'SourceType', Equals: 'Developer Experience' } },
			{ F: 'ScoreEnjoyment', L: 'Enjoyed using it', T: 'likert', HideWhen: { Field: 'SourceType', Equals: 'Developer Experience' } },
			{ F: 'Body', L: 'Feedback', T: 'textarea', Markdown: true }
		],
		Relations:
		[
			{ Render: 'fk', Label: 'Source test run', Field: 'IDTestRun', Target: 'TestRun' },
			{ Render: 'fk', Label: 'Keystone customer', Field: 'IDKeystoneCustomer', Target: 'KeystoneCustomer' },
			{ Render: 'fk', Label: 'About issue', Field: 'IDTargetIssue', Target: 'Issue' },
			{ Render: 'link', Label: 'Work items from this feedback', Join: 'FeedbackWorkItem', SelfField: 'IDFeedback', TargetField: 'IDWorkItem', Target: 'WorkItem' },
			{ Render: 'media', Label: 'Attachments' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	AsBuilt:
	{
		Key: 'AsBuilt', Plural: 'AsBuilts', IDField: 'IDAsBuilt', Title: 'As-Builts', TitleField: 'Title', Icon: 'Package',
		NumberField: 'AsBuiltNumber', Designator: 'AB',
		Connections: true,
		// The closing beat of a work item's story and the unit of promotion: a short description of what a
		// session delivered plus a screenshot (Media OwnerType 'AsBuilt'), bridging the WorkItem to the Routine
		// that validates it and the Feedback that drove it.
		// Only a Validated As-Built may be promoted: plansheet-promote refuses to ship a batch that still has
		// anything unvalidated riding it, so this action is the gate between dev and prod.
		HeaderActions: [{ Label: 'Validate on dev', Method: 'validateAsBuilt' }, { Label: 'Back to releases', Method: 'showReleases' }],
		DefaultSort: 'AsBuiltNumber:desc',
		Columns:
		[
			{ F: 'AsBuiltNumber', L: '#' },
			{ F: 'Title', L: 'Title' },
			{ F: 'Status', L: 'Status', Badge: true, Key: true },
			{ F: 'Environment', L: 'Env' },
			{ F: 'CommitHash', L: 'Commit', Fmt: (pRow) => pRow.CommitHash ? String(pRow.CommitHash).slice(0, 12) : '' }
		],
		Fields:
		[
			{ F: 'Title', L: 'Title', T: 'text' },
			{ F: 'Description', L: 'What was built', T: 'textarea', Markdown: true },
			{ F: 'Status', L: 'Status', T: 'select', O: AsBuiltStatuses },
			{ F: 'Environment', L: 'Environment', T: 'text' },
			{ F: 'Branch', L: 'Branch', T: 'text' },
			{ F: 'CommitHash', L: 'Commit', T: 'text' },
			{ F: 'ImageDigest', L: 'Image digest', T: 'text' }
		],
		Relations:
		[
			{ Render: 'fk', Label: 'Work item', Field: 'IDWorkItem', Target: 'WorkItem' },
			{ Render: 'fk', Label: 'Validated by routine', Field: 'IDRoutine', Target: 'Routine' },
			{ Render: 'fk', Label: 'From feedback', Field: 'IDSourceFeedback', Target: 'Feedback' },
			{ Render: 'fk', Label: 'Validating test run', Field: 'IDValidatingTestRun', Target: 'TestRun' },
			{ Render: 'media', Label: 'Screenshot' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	DeployRequest:
	{
		Key: 'DeployRequest', Plural: 'DeployRequests', IDField: 'IDDeployRequest', Title: 'Deploy Requests', TitleField: 'Action', Icon: 'Rocket',
		NumberField: 'DeployRequestNumber', Designator: 'DR',
		// Browse-only operational record: created by the Releases page's Deploy / Promote buttons (never the + New
		// form) and run by the deploy executor, so it carries no documentation surface (Browse exempts it from the
		// docs / doclink invariants, like Session).
		Browse: true,
		NoCreate: true,
		EmptyText: 'No deploy requests yet. Deploy from the Releases page.',
		DefaultSort: 'CreateDate:desc',
		Columns:
		[
			{ F: 'DeployRequestNumber', L: '#' },
			{ F: 'Action', L: 'Action' },
			// Which environment a deploy touched is the first thing anyone asks, so it stays in concise.
			{ F: 'Environment', L: 'Env', Key: true },
			{ F: 'Status', L: 'Status', Badge: true, Key: true },
			{ F: 'CommitRef', L: 'Commit', Fmt: (pRow) => pRow.CommitRef ? String(pRow.CommitRef).slice(0, 12) : '' },
			{ F: 'CreateDate', L: 'Requested', Fmt: (pRow) => pRow.CreateDate ? String(pRow.CreateDate).slice(0, 16).replace('T', ' ') : '' }
		],
		Fields:
		[
			{ F: 'Action', L: 'Action', T: 'text' },
			{ F: 'Environment', L: 'Environment', T: 'text' },
			{ F: 'CommitRef', L: 'Commit', T: 'text' },
			{ F: 'Status', L: 'Status', T: 'select', O: DeployRequestStatuses },
			{ F: 'ResultLog', L: 'Result log', T: 'textarea' }
		],
		Relations: []
	},
	Workspace:
	{
		Key: 'Workspace', Plural: 'Workspaces', IDField: 'IDWorkspace', Title: 'Workspaces', TitleField: 'Name', Icon: 'Folder',
		NumberField: 'WorkspaceNumber', Designator: 'WS',
		// The SDLC configuration: which repositories this product is built from. A node reads these through
		// GET /1.0/Workspace/Manifest and derives its own paths, so activating a node for this plan sheet needs
		// no edit on the node. THE CONTROL PLANE STORES NO PATH -- that is deliberate and is why there is no
		// path field here.
		EmptyText: 'No repositories declared yet. Add one so a node can work in it without being configured by hand.',
		// By its own number. This read SortOrder:asc, which mirrors the SERVER's manifest sort
		// (Workspace-Service.readWorkspaces) into a client list setting -- and nothing anywhere writes
		// Workspace.SortOrder, so every row is 0 and the sort was a no-op landing rows in raw database
		// order. That is the exact defect the rule at the top of this file exists to prevent, and the
		// number is a column the reader can actually see.
		DefaultSort: 'WorkspaceNumber:asc',
		Columns:
		[
			{ F: 'WorkspaceNumber', L: '#' },
			{ F: 'WorkspaceKey', L: 'Key', Key: true },
			{ F: 'Name', L: 'Name' },
			{ F: 'CheckoutStrategy', L: 'Checkout', Badge: true },
			{ F: 'Trunk', L: 'Trunk' },
			{ F: 'Deployable', L: 'Deploys', Fmt: (pRow) => pRow.Deployable ? 'yes' : '' },
			{ F: 'Status', L: 'Status', Badge: true, Key: true }
		],
		Fields:
		[
			{ F: 'WorkspaceKey', L: 'Key', T: 'text' },
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Description', L: 'Description', T: 'textarea' },
			{ F: 'RepoURL', L: 'Repository URL', T: 'text' },
			{ F: 'CheckoutStrategy', L: 'Checkout strategy', T: 'select', O: WorkspaceCheckoutStrategies },
			{ F: 'IDParentWorkspace', L: 'Inside monorepo', T: 'select', E: 'Workspace' },
			{ F: 'RepoPath', L: 'Path inside that monorepo', T: 'text' },
			{ F: 'Trunk', L: 'Trunk branch', T: 'text' },
			{ F: 'BranchPrefix', L: 'Agent branch prefix', T: 'text' },
			{ F: 'GateJSON', L: 'Gate commands (JSON array)', T: 'textarea' },
			{ F: 'Deployable', L: 'Rides the deploy pipeline', T: 'checkbox' },
			{ F: 'DeployConfigRef', L: 'Deploy config reference', T: 'text' },
			// Ties the manifest order, and therefore which repository is picked when more than one row is
			// flagged Deployable. Unsettable, every row sorted equal and the pick fell to database return
			// order.
			{ F: 'SortOrder', L: 'Sort order', T: 'number' },
			{ F: 'Status', L: 'Status', T: 'select', O: WorkspaceStatuses }
		],
		// AUTHORED, unlike its sibling NodeRegistration: a plan sheet DECLARES a repository -- thirteen
		// typed decisions through POST/PUT /1.0/Workspace, which is why Workspace-Endpoint carries only
		// the derived read -- and a node registers ITSELF. Everything carrying Browse: true holds content
		// that originated outside Plansheet (an uploaded transcript, an upload, a self-registration, a
		// button press), and all of them pair it with NoCreate. So this gets a documentation surface and
		// they do not.
		//
		// Browse: true would have made the same two invariants pass in one line, and would have been the
		// wrong line: the flag has no runtime behaviour at all -- its only readers are those two tests --
		// so it buys a green run by asserting in the file that IS the semantic contract that a workspace
		// is something you only read.
		Relations:
		[
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},


	NodeRegistration:
	{
		Key: 'NodeRegistration', Plural: 'NodeRegistrations', IDField: 'IDNodeRegistration', Title: 'Nodes', TitleField: 'NodeHost', Icon: 'Box',
		NumberField: 'NodeRegistrationNumber', Designator: 'ND',
		// A row-click opens the focused Node workspace (lifecycle actions + Provision); the pencil still opens the
		// generic edit form for CapabilityJSON checkout-path mapping (WI #456), so the detail slug stays.
		OpenAction: 'showNodeWorkspace',
		// Browse-only operational record (like DeployRequest / Session): a node registers itself over the API, so
		// there is no + New form and no documentation surface. Suspend / Resume are the operator actions on it.
		Browse: true,
		NoCreate: true,
		EmptyText: 'No nodes registered yet. A node registers itself over the API when it comes online.',
		DefaultSort: 'CreateDate:desc',
		Columns:
		[
			{ F: 'NodeRegistrationNumber', L: '#' },
			{ F: 'NodeHost', L: 'Host', Key: true },
			{ F: 'Status', L: 'Status', Badge: true, Key: true },
			{ F: 'Roles', L: 'Roles', Fmt: nodeRolesSummary, Computed: true },
			{ F: 'Workspaces', L: 'Workspaces', Fmt: nodeWorkspacesSummary, Computed: true },
			{ F: 'WorkspacePaths', L: 'Checkout paths', Fmt: nodeWorkspacePathsSummary, Computed: true },
			{ F: 'UnresolvedWorkspaces', L: 'Cannot resolve', Fmt: nodeUnresolvedSummary, Computed: true },
			{ F: 'LastSeenDate', L: 'Last seen', Fmt: (pRow) => pRow.LastSeenDate ? String(pRow.LastSeenDate).slice(0, 16).replace('T', ' ') : '' }
		],
		Fields:
		[
			{ F: 'NodeHost', L: 'Host', T: 'text' },
			{ F: 'Status', L: 'Status', T: 'select', O: NodeRegistrationStatuses },
			// A code editor, not a textarea: this blob is how an operator maps a node's checkouts from the web
			// UI (WI #456), and hand-balancing JSON braces in a plain box is how a typo becomes a node that
			// silently stops resolving a workspace.
			{ F: 'CapabilityJSON', L: 'Capability (JSON)', T: 'code', Language: 'json' }
		],
		// Suspend / Resume go through the gated POST /1.0/NodeRegistration/:id/(Suspend|Resume) so the change is
		// entitlement-checked (content.approve) and logged, not a silent field edit.
		HeaderActions: [{ Label: 'Suspend', Method: 'suspendNode' }, { Label: 'Resume', Method: 'resumeNode' }],
		Relations: []
	},
	KeystoneCustomer:
	{
		Key: 'KeystoneCustomer', Plural: 'KeystoneCustomers', IDField: 'IDKeystoneCustomer', Title: 'Customers', TitleField: 'Name', Icon: 'Handshake',
		Connections: true,
		// Any customer you serve and track feedback, calls, and issues for. Tier marks strategic pilots
		// (Keystone) apart from everyone else you support (Standard). Comments attach via CommentThread
		// OwnerType 'KeystoneCustomer' -- exactly the 16-char OwnerType column limit.
		Comments: true,
		// No number on this entity, so no mark and nothing chronological worth leading with: order by name.
		DefaultSort: 'Name:asc',
		Columns: [{ F: 'Name', L: 'Name' }, { F: 'Tier', L: 'Tier', Badge: true, Key: true }, { F: 'ContactName', L: 'Contact' }, { F: 'Status', L: 'Status', Badge: true, Key: true }],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Tier', L: 'Tier', T: 'select', O: KeystoneCustomerTiers },
			{ F: 'ContactName', L: 'Primary contact name', T: 'text' },
			{ F: 'ContactEmail', L: 'Primary contact email', T: 'text' },
			{ F: 'Summary', L: 'Summary', T: 'text' },
			{ F: 'Description', L: 'Description', T: 'textarea', Markdown: true },
			{ F: 'Status', L: 'Status', T: 'select', O: KeystoneCustomerStatuses }
		],
		Relations:
		[
			{ Render: 'inverse-fk', Label: 'Contacts', Source: 'Contact', SourceField: 'IDKeystoneCustomer', SubField: 'Kind',
				Create: { Label: 'New contact', Prefill: {} } },
			{ Render: 'inverse-fk', Label: 'Calls', Source: 'Call', SourceField: 'IDKeystoneCustomer', SubField: 'Channel',
				Create: { Label: 'Log a call', Prefill: {} } },
			{ Render: 'inverse-fk', Label: 'Issues', Source: 'Issue', SourceField: 'IDKeystoneCustomer', SubField: 'Status' },
			{ Render: 'inverse-fk', Label: 'Feedback from this customer', Source: 'Feedback', SourceField: 'IDKeystoneCustomer', SubField: 'SourceType',
				Create: { Label: 'New feedback', Prefill: { SourceType: 'Customer Conversation' } } },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	Issue:
	{
		Key: 'Issue', Plural: 'Issues', IDField: 'IDIssue', Title: 'Issues', TitleField: 'Title', Icon: 'AlertTriangle',
		NumberField: 'IssueNumber', Designator: 'I',
		// Comments, shared checklists, and evidence media attach to an issue (OwnerType 'Issue').
		Comments: true,
		Checklist: true,
		Connections: true,
		// Self-assign or spin a draft vision out of an issue, from the detail header.
		HeaderActions: [{ Label: 'Assign to me', Method: 'assignIssueToMe' }, { Label: 'Assign...', Method: 'assignIssue' }, { Label: 'Spawn a vision', Method: 'spawnVisionFromIssue' }, { Label: 'Spawn work item', Method: 'spawnWorkItemFromIssue' }],
		DefaultSort: 'IssueNumber:desc',
		Columns:
		[
			{ F: 'IssueNumber', L: '#' },
			{ F: 'Title', L: 'Title' },
			{ F: 'Status', L: 'Status', Badge: true, Key: true },
			{ F: 'Priority', L: 'Priority' },
			// Severity is what triage sorts on, so it earns its place in the concise scan.
			{ F: 'Severity', L: 'Severity', Key: true },
			{ F: 'Reliability', L: 'Reliability' }
		],
		Fields:
		[
			{ F: 'Title', L: 'Title', T: 'text' },
			{ F: 'Status', L: 'Status', T: 'select', O: IssueStatuses },
			{ F: 'Priority', L: 'Priority', T: 'select', O: Priorities },
			{ F: 'Severity', L: 'Severity', T: 'select', O: IssueSeverities },
			{ F: 'Reliability', L: 'Reliability', T: 'select', O: IssueReliabilities },
			{ F: 'Description', L: 'Description', T: 'textarea', Markdown: true }
		],
		Relations:
		[
			{ Render: 'fk', Label: 'Issue type', Field: 'IDIssueType', Target: 'IssueType' },
			{ Render: 'fk', Label: 'Affected customer', Field: 'IDKeystoneCustomer', Target: 'KeystoneCustomer' },
			{ Render: 'fk', Label: 'Reported by', Field: 'IDReportedByContact', Target: 'Contact' },
			{ Render: 'fk', Label: 'From call', Field: 'IDSourceCall', Target: 'Call' },
			{ Render: 'fk', Label: 'Spawned vision', Field: 'IDTargetVision', Target: 'Vision' },
			{ Render: 'link', Label: 'Work items addressing this', Join: 'IssueWorkItem', SelfField: 'IDIssue', TargetField: 'IDWorkItem', Target: 'WorkItem' },
			{ Render: 'media', Label: 'Screenshots & evidence' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	Expectation:
	{
		Key: 'Expectation', Plural: 'Expectations', IDField: 'IDExpectation', Title: 'Expectations', TitleField: 'Title', Icon: 'ListChecks',
		NumberField: 'ExpectationNumber', Designator: 'EXP',
		// What a served customer expects delivered, in their words. Forward-looking, NOT a problem (Issue);
		// fulfilled by one or more Deliverables. Comments + connections attach (OwnerType 'Expectation').
		Comments: true,
		Connections: true,
		DefaultSort: 'ExpectationNumber:desc',
		Columns:
		[
			{ F: 'ExpectationNumber', L: '#' },
			{ F: 'Title', L: 'What they expect' },
			{ F: 'Status', L: 'Status', Badge: true, Key: true }
		],
		Fields:
		[
			{ F: 'Title', L: 'Title', T: 'text' },
			{ F: 'Status', L: 'Status', T: 'select', O: ExpectationStatuses },
			{ F: 'Description', L: 'Description', T: 'textarea', Markdown: true }
		],
		Relations:
		[
			{ Render: 'fk', Label: 'Customer', Field: 'IDKeystoneCustomer', Target: 'KeystoneCustomer' },
			{ Render: 'inverse-fk', Label: 'Deliverables', Source: 'Deliverable', SourceField: 'IDExpectation', SubField: 'Status',
				Create: { Label: 'New deliverable', Prefill: {} } },
			{ Render: 'fk', Label: 'Spawned vision', Field: 'IDTargetVision', Target: 'Vision' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	Deliverable:
	{
		Key: 'Deliverable', Plural: 'Deliverables', IDField: 'IDDeliverable', Title: 'Deliverables', TitleField: 'Title', Icon: 'Box',
		NumberField: 'DeliverableNumber', Designator: 'DLV',
		HeaderActions: [{ Label: 'Compose blueprint', Method: 'composeDeliverableBlueprint' }],
		// The fulfilment unit a customer receives (renamed "Solution" for software via the Drafting Kit
		// terms). Nests into sub-deliverables; moves through a stage pipeline; bundles a Blueprint; checks off.
		Comments: true,
		Checklist: true,
		Connections: true,
		DefaultSort: 'DeliverableNumber:desc',
		Columns:
		[
			{ F: 'DeliverableNumber', L: '#' },
			{ F: 'Title', L: 'Title' },
			{ F: 'Status', L: 'Stage', Badge: true, Key: true }
		],
		Fields:
		[
			{ F: 'Title', L: 'Title', T: 'text' },
			{ F: 'Status', L: 'Stage', T: 'select', O: DeliverableStages },
			{ F: 'Description', L: 'Description', T: 'textarea', Markdown: true }
		],
		Relations:
		[
			{ Render: 'fk', Label: 'Customer', Field: 'IDKeystoneCustomer', Target: 'KeystoneCustomer' },
			{ Render: 'fk', Label: 'Fulfils expectation', Field: 'IDExpectation', Target: 'Expectation' },
			{ Render: 'fk', Label: 'Part of', Field: 'IDParentDeliverable', Target: 'Deliverable' },
			{ Render: 'inverse-fk', Label: 'Sub-deliverables', Source: 'Deliverable', SourceField: 'IDParentDeliverable', SubField: 'Status',
				Create: { Label: 'New sub-deliverable', Prefill: {} } },
			{ Render: 'fk', Label: 'Blueprint', Field: 'IDBlueprint', Target: 'Blueprint' },
			{ Render: 'fk', Label: 'From vision', Field: 'IDSourceVision', Target: 'Vision' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	// A first-class, standalone blueprint: a named drafting title block you compose + reuse, beyond a single
	// work item. Name + Description + comments today; the title-block composer (pin artifacts to it) is the
	// next step -- it reuses the work item's renderer pointed at this blueprint's own pinned items.
	Blueprint:
	{
		Key: 'Blueprint', Plural: 'Blueprints', IDField: 'IDBlueprint', Title: 'Blueprints', TitleField: 'Name', Icon: 'FileText',
		NumberField: 'BlueprintNumber', Designator: 'BP',
		// Every blueprint, composed and entity-owned alike, because any of them can be made the plan sheet's
		// cover from here (WI #339 + #340). The For column is what keeps that legible: an entity's blueprint
		// says which entity, a composed one says so.
		HeaderActions: [{ Label: 'Compose', Method: 'composeBlueprint' }, { Label: 'Set as cover', Method: 'setPlansheetCover' }],
		Comments: true,
		DefaultSort: 'BlueprintNumber:desc',
		// Only the mark and the name, both of which are always shown, so the two modes are identical here.
		Columns:
		[
			{ F: 'BlueprintNumber', L: '#' },
			{ F: 'Name', L: 'Name' },
			// Which thing this blueprint is OF. Without it every entity blueprint reads as an anonymous name
			// in a list that also holds hand-composed ones, and there is no way to tell them apart.
			// Key: it survives into concise mode, because telling an entity's blueprint from a hand-composed
			// one is the whole reason this column exists -- hiding it is what makes the list unreadable.
			{ F: 'OwnerType', L: 'For', Key: true, Fmt: (pRow) => (pRow && pRow.OwnerType) ? ({ WorkItem: 'Work item', Vision: 'Vision', Module: 'Module', Feature: 'Feature' })[pRow.OwnerType] || pRow.OwnerType : 'Composed' }
		],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Description', L: 'Description', T: 'textarea', Markdown: true }
		],
		Relations:
		[
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	IssueType:
	{
		Key: 'IssueType', Plural: 'IssueTypes', IDField: 'IDIssueType', Title: 'Issue Types', TitleField: 'Name', Icon: 'Tags',
		DefaultSort: 'Name:asc',
		Columns: [{ F: 'Name', L: 'Name' }, { F: 'Category', L: 'Category', Badge: true, Key: true }],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Category', L: 'Category', T: 'text' },
			{ F: 'Color', L: 'Color', T: 'text' }
		],
		Relations:
		[
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	Contact:
	{
		Key: 'Contact', Plural: 'Contacts', IDField: 'IDContact', Title: 'Contacts', TitleField: 'Name', Icon: 'Users',
		Comments: true,
		DefaultSort: 'Name:asc',
		Columns: [{ F: 'Name', L: 'Name' }, { F: 'Kind', L: 'Kind', Badge: true, Key: true }, { F: 'Email', L: 'Email', Key: true }, { F: 'Phone', L: 'Phone' }],
		Fields:
		[
			{ F: 'Name', L: 'Name', T: 'text' },
			{ F: 'Kind', L: 'Kind', T: 'select', O: ContactKinds },
			{ F: 'Email', L: 'Email', T: 'text' },
			{ F: 'Phone', L: 'Phone', T: 'text' },
			{ F: 'Role', L: 'Role', T: 'text' },
			{ F: 'Notes', L: 'Notes', T: 'textarea' }
		],
		Relations:
		[
			{ Render: 'fk', Label: 'Customer', Field: 'IDKeystoneCustomer', Target: 'KeystoneCustomer' },
			{ Render: 'inverse-fk', Label: 'Calls with this contact', Source: 'Call', SourceField: 'IDContact', SubField: 'Channel' },
			{ Render: 'inverse-fk', Label: 'Issues reported', Source: 'Issue', SourceField: 'IDReportedByContact', SubField: 'Status' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	Call:
	{
		Key: 'Call', Plural: 'Calls', IDField: 'IDCall', Title: 'Calls', TitleField: 'Subject', Icon: 'Phone',
		NumberField: 'CallNumber', Designator: 'CL',
		Comments: true,
		DefaultSort: 'CallDate:desc',
		Columns:
		[
			{ F: 'CallNumber', L: '#' },
			{ F: 'Subject', L: 'Subject' },
			{ F: 'Channel', L: 'Channel', Badge: true, Key: true },
			{ F: 'Direction', L: 'Direction' },
			{ F: 'CallDate', L: 'When', Fmt: (pRow) => pRow.CallDate ? String(pRow.CallDate).slice(0, 10) : '', Key: true }
		],
		Fields:
		[
			{ F: 'Subject', L: 'Subject', T: 'text' },
			{ F: 'Channel', L: 'Channel', T: 'select', O: CallChannels },
			{ F: 'Direction', L: 'Direction', T: 'select', O: CallDirections },
			{ F: 'Notes', L: 'Notes', T: 'textarea', Markdown: true }
		],
		Relations:
		[
			{ Render: 'fk', Label: 'Customer', Field: 'IDKeystoneCustomer', Target: 'KeystoneCustomer' },
			{ Render: 'fk', Label: 'Contact', Field: 'IDContact', Target: 'Contact' },
			{ Render: 'inverse-fk', Label: 'Issues from this call', Source: 'Issue', SourceField: 'IDSourceCall', SubField: 'Status' },
			{ Render: 'docs', Label: 'Documentation' },
			{ Render: 'doclink', Label: 'Foundational documentation' }
		]
	},
	Session:
	{
		Key: 'Session', Plural: 'SessionFiles', IDField: 'IDSessionFile', Title: 'Sessions', TitleField: 'Title', Icon: 'MessageSquare',
		// Browse-only: a Claude session is an uploaded transcript you read, not an
		// authored SDLC artifact. It carries no documentation or relation surface
		// (the Browse flag exempts it from the docs/doclink invariants). You add one
		// by uploading a .zip (CreateButton), and Open hands it to the session
		// viewer rather than a CRUD detail (OpenAction). Edit just renames it.
		Browse: true,
		NoCreate: true,
		NumberField: 'SessionNumber',
		Designator: 'SN',
		// A session is cited as PROVENANCE on a printed sheet -- "where did this come from" -- which is
		// what the mark is for (WI #385). It is not cited in the app: nobody says "open SN-014", they
		// scan the list by title and date. So the connections cell keeps the readable title.
		//
		// That cell prefers the MARK by default, and deliberately: a grid is scanned rather than read,
		// and three marks cross-reference at a glance where three truncated sentences cannot. The
		// argument holds for artifacts people cite by mark and inverts for one nobody does, where it
		// would trade three readable titles for three opaque numbers. The detail row underneath still
		// leads with the mark, so nothing is lost -- see PictView-Connections.
		ChipPrefersMark: false,
		CreateButton: { Label: 'Upload session', Action: 'showUploader' },
		OpenAction: 'showSession',
		EmptyText: 'No sessions yet. Upload a Claude session export (.zip) to browse it here.',
		DefaultSort: 'SessionStartedAt:desc',
		Columns:
		[
			{ F: 'SessionNumber', L: '#' },
			{ F: 'Title', L: 'Title' },
			{ F: 'SessionStartedAt', L: 'Started', Fmt: (pRow) => pRow.SessionStartedAt ? String(pRow.SessionStartedAt).slice(0, 10) : '', Key: true },
			{ F: 'TotalDurationMs', L: 'Length', Fmt: (pRow) =>
				{
					let tmpS = Math.round((pRow.TotalDurationMs || 0) / 1000);
					if (!tmpS) { return ''; }
					let tmpH = Math.floor(tmpS / 3600);
					let tmpM = Math.floor((tmpS % 3600) / 60);
					return tmpH ? (tmpH + 'h ' + tmpM + 'm') : (tmpM ? (tmpM + 'm') : (tmpS + 's'));
				} },
			{ F: 'PromptCount', L: 'Prompts' },
			{ F: 'ToolCallCount', L: 'Tools' },
			{ F: 'Model', L: 'Model', Badge: true }
		],
		Fields:
		[
			{ F: 'Title', L: 'Title', T: 'text' }
		],
		Relations: []
	},

	Media:
	{
		Key: 'Media', Plural: 'Medias', IDField: 'IDMedia', Title: 'Media', TitleField: 'Title', Icon: 'Image',
		// Media is SUBSTANCE. Screenshots and recordings hang off work items, issues and visions, and they
		// land in the attachments of a printed set -- but the entity had no number, so EntityMark returned
		// empty for it and an attachment carrying a screenshot could not be cited. "See MD-042" was
		// unsayable (WI #386).
		//
		// The alternative considered and rejected was set-local marks (X-1, X-2) assigned at compile time.
		// Those break the moment set membership changes, so a customer's annotated Rev A stops matching
		// Rev B -- exactly the stability a designator exists to provide.
		//
		// Browse-only, like Sessions: a media row is an uploaded artifact you look at, not an authored
		// one. It is created by an upload (POST /1.0/Media/New) rather than from this list, and the Browse
		// flag exempts it from the docs and doclink relation invariants that an authored entity carries.
		Browse: true,
		NoCreate: true,
		NumberField: 'MediaNumber',
		Designator: 'MD',
		OpenAction: 'showMedia',
		EmptyText: 'No media yet. Screenshots, drawings and recordings attached anywhere in this plan sheet appear here.',
		// Newest first, by the date the list actually shows.
		DefaultSort: 'CreateDate:desc',
		Columns:
		[
			{ F: 'MediaNumber', L: '#' },
			{ F: 'Title', L: 'Title' },
			{ F: 'MediaType', L: 'Kind', Badge: true, Key: true },
			// What it hangs off. Media is polymorphic (OwnerType + IDOwner), and which KIND of thing owns
			// a screenshot is the fact a person scanning this list wants first.
			{ F: 'OwnerType', L: 'Attached to', Key: true },
			{ F: 'OriginalFileName', L: 'File', DefaultHidden: true },
			{ F: 'FileSizeBytes', L: 'Size', DefaultHidden: true, Fmt: (pRow) =>
				{
					let tmpBytes = Number(pRow.FileSizeBytes) || 0;
					if (!tmpBytes) { return ''; }
					if (tmpBytes < 1024) { return tmpBytes + ' B'; }
					if (tmpBytes < (1024 * 1024)) { return Math.round(tmpBytes / 1024) + ' KB'; }
					return (Math.round((tmpBytes / (1024 * 1024)) * 10) / 10) + ' MB';
				} },
			{ F: 'CreateDate', L: 'Added', Fmt: (pRow) => pRow.CreateDate ? String(pRow.CreateDate).slice(0, 10) : '' }
		],
		Fields:
		[
			{ F: 'Title', L: 'Title', T: 'text' },
			{ F: 'MediaType', L: 'Kind', T: 'select', O: MediaTypes }
		],
		Relations: []
	}
};

// The entity types a WorkItem can target (its "About"), in picker order.
const WorkItemTargetTypes = ['None', 'Vision', 'Module', 'Feature'];

module.exports = { DefaultBoardLanes, BoardLanes, BoardLaneLabels, Priorities, WorkItemTypes, WorkItemTypeIcons, FeedbackSourceIcons, RelationTypes, WorkOutcomes, QCReasonCodes, MediaTypes,
	TaskStatuses, TaskOutcomeKinds, RequestStatuses,
	RoutineStatuses, TestRunStatuses, TestRunStepOutcomes, AsBuiltStatuses, DeployRequestStatuses, FeedbackSourceTypes, KeystoneCustomerStatuses, KeystoneCustomerTiers,
	IssueStatuses, IssueSeverities, IssueReliabilities, ContactKinds, CallChannels, CallDirections,
	formatLikert, formatLikertShort, Joins, Entities, WorkItemTargetTypes };
